import React from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Heart, MessageCircle, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { Progress } from './Progress';
import { PlatformIcon } from './PlatformIcon';
import type { SocialInsight } from '../types/expert';
import { formatCompact, formatDate, scoreColor, scoreLabel } from '../utils/expert';
type SocialInsightPanelProps = {
  social: SocialInsight;
};
export function SocialInsightPanel({
  social
}: SocialInsightPanelProps) {
  const totalFollowers = social.accounts.reduce((s, a) => s + a.followers, 0);
  return <div className="flex flex-col gap-4" data-id="element-550" data-mp="34agn4aci7ddw">
      <Card data-id="element-551" data-mp="34b87ep0nhoov">
        <CardHeader data-id="element-552" data-mp="34c1gvxqzusap">
          <div className="flex flex-wrap items-center gap-2" data-id="element-553" data-mp="348dpb4540fym">
            <span className="flex size-7 items-center justify-center rounded-md bg-violet-100 text-violet-700" data-id="element-554" data-mp="ekcxw8mq85k0">
              <Sparkles className="size-4" aria-hidden="true" data-id="element-555" data-mp="jio9anso6o4c" />
            </span>
            <CardTitle data-id="element-556" data-mp="1axdhxooxrpk8">AI holistic overview</CardTitle>
            <Badge variant="outline" className="ml-auto" data-id="element-557" data-mp="15zkaytft6riw">Updated 2 hours ago</Badge>
          </div>
          <CardDescription data-id="element-558" data-mp="11xyruqcolh09">Generated from public activity across all connected platforms in the last 12 months.</CardDescription>
        </CardHeader>
        <CardContent data-id="element-559" data-mp="wy42ir8k0zhb">
          <div className="grid gap-6 lg:grid-cols-[200px_1fr]" data-id="element-560" data-mp="x1002eghpm58">
            <div className="flex flex-col items-center justify-center rounded-xl border bg-muted/30 p-5 text-center" data-id="element-561" data-mp="s0udvdszoruj">
              <ScoreRing score={social.overallScore} data-id="element-562" data-mp="n7j8lh3tsj4q" />
              <p className={`mt-2 text-sm font-semibold ${scoreColor(social.overallScore)}`} data-id="element-563" data-mp="i9hn6pixg7q3">{scoreLabel(social.overallScore)}</p>
              <p className="text-xs text-muted-foreground" data-id="element-564" data-mp="e9paq1qexhfg">Social engagement score</p>
              <div className="mt-3 flex gap-4 text-xs" data-id="element-565" data-mp="989q2as5dcxl">
                <div data-id="element-566" data-mp="9b5ri937ejqs">
                  <p className="font-semibold tabular-nums" data-id="element-567" data-mp="49opga8zgr8m">{formatCompact(totalFollowers)}</p>
                  <p className="text-muted-foreground" data-id="element-568" data-mp="3qsa01o8f5ch3">Reach</p>
                </div>
                <div data-id="element-569" data-mp="3vr3djgnph3h9">
                  <p className="font-semibold" data-id="element-570" data-mp="3vrmlq5nk0di3">{social.sentiment}</p>
                  <p className="text-muted-foreground" data-id="element-571" data-mp="3voqneg7snmt4">Sentiment</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4" data-id="element-572" data-mp="3lsdtpic918yd">
              <p className="text-sm leading-relaxed" data-id="element-573" data-mp="3lt5o4pu410n2">{social.summary}</p>
              <div className="flex flex-wrap gap-1.5" data-id="element-574" data-mp="deexqgpa1nkv">
                {social.topics.map(t => <Badge key={t} variant="secondary" data-id="element-575" data-mp="iavm9c0e23yh">#{t}</Badge>)}
              </div>
              <div className="grid gap-3 sm:grid-cols-2" data-id="element-576" data-mp="3mizn5n6xxgn">
                <div className="rounded-lg border p-3" data-id="element-577" data-mp="8m3yzh69w7tw">
                  <p className="text-xs font-medium text-muted-foreground" data-id="element-578" data-mp="8kkipqkm9ws4">Strengths</p>
                  <ul className="mt-2 space-y-1.5" data-id="element-579" data-mp="waeu03pafda9">
                    {social.strengths.map(s => <li key={s} className="flex gap-2 text-sm" data-id="element-580" data-mp="113zhv2x62qvq">
                        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" aria-hidden="true" data-id="element-581" data-mp="2lsbrk92ckpmc" />
                        {s}
                      </li>)}
                  </ul>
                </div>
                <div className="rounded-lg border p-3" data-id="element-582" data-mp="2vid9sk80rdtc">
                  <p className="text-xs font-medium text-muted-foreground" data-id="element-583" data-mp="2qja9fd2zhvrp">Watch-outs</p>
                  <ul className="mt-2 space-y-1.5" data-id="element-584" data-mp="22tzc65kuejib">
                    {social.watchouts.map(w => <li key={w} className="flex gap-2 text-sm" data-id="element-585" data-mp="1xvo2pvygmb0m">
                        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden="true" data-id="element-586" data-mp="1xxhc3j0gj9cb" />
                        {w}
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div data-id="element-587" data-mp="3j8hg4zcldro1">
        <h3 className="text-sm font-semibold" data-id="element-588" data-mp="3j90njm4m8xnt">Connected platforms ({social.accounts.length})</h3>
        <div className="mt-3 grid gap-4 md:grid-cols-2" data-id="element-589" data-mp="idji5bed8nwu">
          {social.accounts.map(a => <Card key={a.platform} size="sm" data-id="element-590" data-mp="mebpfz3ogiqh">
              <CardContent data-id="element-591" data-mp="mfdamweeqllx">
                <div className="flex items-center gap-3" data-id="element-592" data-mp="mgoe0v3xxrci">
                  <PlatformIcon platform={a.platform} className="size-9" data-id="element-593" data-mp="rd6f9l02b7k3" />
                  <div className="min-w-0 flex-1" data-id="element-594" data-mp="wdaz9l06ikf2">
                    <p className="text-sm font-semibold" data-id="element-595" data-mp="way9ilo6j5rg">{a.platform}</p>
                    <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline" data-id="element-596" data-mp="11bmb21a1b4zx">
                      {a.handle}
                      <ExternalLink className="size-3" aria-hidden="true" data-id="element-597" data-mp="1f5myhyvjd5kz" />
                    </a>
                  </div>
                  <div className="text-right" data-id="element-598" data-mp="1p1q45epljmco">
                    <p className={`text-lg font-semibold tabular-nums ${scoreColor(a.score)}`} data-id="element-599" data-mp="1p2hv2c890vwm">{a.score}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-600" data-mp="39lbyhs6v9mfi">score</p>
                  </div>
                </div>
                <Progress value={a.score} className="mt-3" aria-label={`${a.platform} engagement score`} data-id="element-601" data-mp="34n0ptksb5hyv" />
                <dl className="mt-3 grid grid-cols-3 gap-2 text-xs" data-id="element-602" data-mp="2zne9h2h6nllu">
                  <div data-id="element-603" data-mp="2uo1hy4fd9qcx">
                    <dt className="text-muted-foreground" data-id="element-604" data-mp="2uouq99tz4nx6">Followers</dt>
                    <dd className="font-semibold tabular-nums" data-id="element-605" data-mp="3s9p8i8p55egj">{formatCompact(a.followers)}</dd>
                  </div>
                  <div data-id="element-606" data-mp="3nd8l5gprn44i">
                    <dt className="text-muted-foreground" data-id="element-607" data-mp="3ik5tje4ju4qy">Engagement</dt>
                    <dd className="font-semibold tabular-nums" data-id="element-608" data-mp="3iicmhxq0xiif">{a.engagementRate}%</dd>
                  </div>
                  <div data-id="element-609" data-mp="3djhzejl0x7si">
                    <dt className="text-muted-foreground" data-id="element-610" data-mp="evvrhk52rxpw">Posts / mo</dt>
                    <dd className="font-semibold tabular-nums" data-id="element-611" data-mp="ex6qz7rhmjb7">{a.postsPerMonth}</dd>
                  </div>
                </dl>
                <div className="mt-3 rounded-lg bg-muted/50 p-3" data-id="element-612" data-mp="24by2k6r7t9vs">
                  <p className="text-[11px] font-medium text-muted-foreground" data-id="element-613" data-mp="24eu03tz5hwjp">Latest post · {formatDate(a.latestPost.date)}</p>
                  <p className="mt-1 line-clamp-2 text-sm" data-id="element-614" data-mp="1un6ei9p9z9q5">{a.latestPost.text}</p>
                  <div className="mt-2 flex gap-3 text-xs text-muted-foreground" data-id="element-615" data-mp="1zlraty1ep99b">
                    <span className="inline-flex items-center gap-1" data-id="element-616" data-mp="1zjelgztbjwm8">
                      <Heart className="size-3" aria-hidden="true" data-id="element-617" data-mp="1lkez5yta3tt8" />{formatCompact(a.latestPost.likes)}
                    </span>
                    <span className="inline-flex items-center gap-1" data-id="element-618" data-mp="1pl8ngtamvamn">
                      <MessageCircle className="size-3" aria-hidden="true" data-id="element-619" data-mp="1bpda12z06rqn" />{formatCompact(a.latestPost.comments)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </div>;
}
function ScoreRing({
  score
}: {
  score: number;
}) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - score / 100 * circumference;
  return <div className="relative size-28" data-id="element-620" data-mp="ufj21matpoio">
      <svg viewBox="0 0 100 100" className="size-full -rotate-90" aria-hidden="true" data-id="element-621" data-mp="ucdfoetkx0qn">
        <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="8" className="stroke-muted" data-id="element-622" data-mp="194p036xxd8oz" />
        <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className={`stroke-current ${scoreColor(score)}`} data-id="element-623" data-mp="14bcobxecgwdn" />
      </svg>
      <span className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-624" data-mp="1s05n01mcpiby">
        <span className="text-3xl font-semibold tabular-nums" data-id="element-625" data-mp="1n3x2e8pdwwxe">{score}</span>
        <span className="text-[10px] text-muted-foreground" data-id="element-626" data-mp="3tumuxkq1sfx3">/ 100</span>
      </span>
    </div>;
}